
/**
 * 级联组件单选
 */
import { Vue, Component, Prop, Watch } from 'vue-property-decorator'
import { ITreeOption, NO_LIMIT } from '@/const/fastCreation'
import Popup from '@/components/popup/popup.vue'

// tree组件暴露的值节点
interface TreeValueNode {
  value: string | number;
  children?: TreeValueNode[];
  status: 'all' | 'partial';
}

@Component({
  name: 'popup-tree',
  components: {
    Popup
  }
})
export default class PopupCascader extends Vue {
  @Prop({ type: Boolean, default: false, required: true }) readonly show: boolean
  @Prop({ type: String, default: '' }) readonly title: string
  @Prop({ type: Boolean, default: true }) flatMode: boolean
  @Prop({ type: Array, default: () => [] }) readonly value: Array<string | number>
  @Prop({ type: Array, default: () => [] }) readonly options: Array<ITreeOption>
  @Prop({ type: Object, required: true }) treeNodesMap: object
  @Prop({ type: Boolean, default: false, required: false }) dynamicHeader: boolean
  // selected: any[] = this.transformValue(this.value);
  selected = [];
  init = true
  @Watch('value')
  onValuesChanged(val: Array<string | number>): void {
    // this.selected = this.transformValue(val);
    this.selected = this.transform2Leafs(val)
    this.origin = this.selected
  }

  @Watch('options')
  onOptionsChanged(newVal, oldVal): void {
    if (oldVal.length === 0 && newVal.length !== 0) {
      this.selected = this.transform2Leafs(this.value)
      this.init = false
      return;
    }
  }
  mounted() {
    this.selected = this.transform2Leafs(this.value)
    this.origin = this.selected
  }

  onConfirm(): void {
    this.$emit('confirm')
  }

  transformValue(val: Array<string | number> = []): any[] {
    if (this.flatMode) {
      return val
    } else {
      return val.map(item=> ({
        value: item,
        status: 'all',
      }))
    }
  }

  handleChange(treeValues: any): void {
    const oldVal = this.selected
    const newVal = treeValues

    if (oldVal.indexOf(NO_LIMIT.value) > -1) {
      this.selected = (newVal as string[]).filter(v=> v !== NO_LIMIT.value)
      return
    }

    if (oldVal.indexOf(NO_LIMIT.value) === -1 && newVal.indexOf(NO_LIMIT.value) > -1) {
      this.selected = [NO_LIMIT.value]
      return
    }

    this.selected = newVal
  }

  // 解析组件暴露的tree结构数据，放到传入的result中
  parseTreeValues(
    treeValues: Array<TreeValueNode> = [],
    depth = 0,
    result: {
      mergedValues: Array<string | number>;
      throughedValues: Array<Array<string | number>>;
      checkedLeafs: Array<string | number>;
    }
  ): void {
    if (!treeValues.length) {
      return
    }
    result.throughedValues[depth] = result.throughedValues[depth] || []

    treeValues.forEach(node => {
      const { children = [], value, status  }  = node
      // 叶子节点
      if (!children.length) {
        result.checkedLeafs.push(value)
      }

      if (status === 'all') {
        // 节点全选，子树不再遍历
        result.mergedValues.push(value)
        result.throughedValues[depth].push(value)
      } else if (status === 'partial') {
        // 节点部分选中，子树需要继续遍历
        result.throughedValues[depth].push(value)
        this.parseTreeValues(children, depth + 1, result)
      }
    })
  }

  get leafIdsMap() {
    const result = {}
    const nodes = Object.values(this.treeNodesMap)

    function getLeafIds(id: string | number): Array<string | number> {
      let leafIds: Array<string | number> = []
      const children = nodes.filter(item => item.parentId === id)
      if (children.length === 0) {
        return [id]
      }
      children.forEach((item) => {
        leafIds = leafIds.concat(getLeafIds(item.value))
      })
      return leafIds
    }

    for (let i = 0; i < nodes.length; i += 1) {
      const { value } = nodes[i]
      result[value] = getLeafIds(value)
    }
    return result
  }

  get throughedValues() {
    const result = []
    const throughedIds = this.checkedToThroughValue(this.selected) || []
    throughedIds.forEach((id) => {
      const level = this.getNodeLevel(id)
      result[level] = result[level] || []
      result[level].push(id)
    })
    return result
  }

  get mergedValues() {
    return this.checkedToValue(this.selected)
  }

  checkedToValue(checked) {
    // source为异步加载时，可能先拿到了value，但source还在拉取中，此时value需要保留住
    if (!Object.keys(this.treeNodesMap).length) {
      return checked.slice()
    }

    let orphans = checked.slice()
    let orphanSet = new Set(orphans)
    const result = []

    const getLowestCheckedAncestor = (id) => {
      const { parentId } = this.treeNodesMap[id] || {}
      if (!parentId) {
        return id
      }
      const leafs = this.leafIdsMap[parentId] || []
      const isAllSelected = leafs.every(id => orphanSet.has(id))

      if (!isAllSelected) {
        return id
      }
      return getLowestCheckedAncestor(parentId)
    }

    const removeAncestorLeafs = (ancestor) => {
      const leafIds = this.leafIdsMap[ancestor] || [ancestor]
      orphans = orphans.filter(id => !leafIds.includes(id))
      orphanSet = new Set(orphans)
    }

    while (orphans.length) {
      const first = orphans[0]
      const ancestor = getLowestCheckedAncestor(first)
      result.push(ancestor)
      removeAncestorLeafs(ancestor)
    }
    return result
  }

  checkedToThroughValue(checked) {
    let orphans = checked
    const result = []
    // 按子树规模倒序排列
    const treeNodes = Object.values(this.treeNodesMap).sort((prev, next) => {
      return (this.leafIdsMap[next.id] || []).length - (this.leafIdsMap[prev.id] || []).length
    })
    for (let i = 0; i < treeNodes.length; i += 1) {
      if (orphans.length === 0) {
        break
      }
      const { id = '' } = this.treeNodesMap[treeNodes[i].id] || {}
      const leafIds = this.leafIdsMap[id] || []
      if (this.isLeaf(id)) {
        // 叶子节点单选
        if (orphans.includes(id)) {
          result.push(id)
          orphans = orphans.filter(s => s !== id)
        }
      } else {
        // 子节点全选，使用合并后的父节点
        // eslint-disable-next-line
        if (leafIds.every((leaf) => orphans.includes(leaf))) {
          result.push(id)
          orphans = orphans.filter(s => !leafIds.includes(s))
          // eslint-disable-next-line
        } else if (leafIds.some((leaf) => orphans.includes(leaf))) {
          // 子节点部分选择
          result.push(id)
          orphans = orphans.filter(s => s !== id)
        }
      }
    }
    return result
  }
  // 是否叶子结点
  isLeaf(id) {
    const leafIds = this.leafIdsMap[id] || []
    return leafIds[0] === id && leafIds.length === 1
  }
  // 获取节点深度
  getNodeLevel(id) {
    let result = 0
    const { parentId } = this.treeNodesMap[id] || {}
    if (!parentId) {
      return 0
    }
    result += 1
    result += this.getNodeLevel(parentId)
    return result
  }

  transform2Leafs(values = []) {
    const leafs = values.reduce((result, value)=> {
      return result.concat(this.leafIdsMap[value] || [value])
    },[])
    return Array.from(new Set(leafs))
  }

  handleConfirm(): void {
    this.origin = this.selected
    if (this.flatMode) {
      this.$emit('confirm', this.selected)
    } else {
      this.$emit('confirm', {
        mergedValues: this.mergedValues,
        throughedValues: this.throughedValues,
        checkedLeafs: this.selected,
      })
    }

    this.closePopup()
  }

  closePopup(): void {
    // 解决数据变更取消时回显出入
    this.selected = this.origin
    this.$emit('close')
  }

  handleCancel (): void {
    this.closePopup()
  }
}

